/**
 * Pure audio helpers + constants shared by the Studio hooks.
 * No React, no hook state — safe to import anywhere.
 */

export const SAMPLE_RATE = 44100;
export const LIVE_PEAKS_PER_SEC = 10; // must match Timeline PX_PER_SEC

// Ambient noise check (dBFS) — runs automatically when the Studio opens.
// Quiet room ≈ -55 to -60 dBFS.
export const NOISE_WARN_DB = -45;   // warn above this
export const NOISE_BLOCK_DB = -30;  // hard block above this
export const NOISE_CHECK_MS = 1500; // sampling window

// -------- Latency compensation ---------------------------------------------
// The mic captures the artist's voice AFTER the artist heard the beat, so the
// recorded vocal blob is late by a full roundtrip (speaker delay + mic delay).
export const LS_LATENCY_MS = 't4t_latency_ms';
export const MAX_COMPENSATION_S = 0.5;
// Empirical bias added on top of the measured mic-round-trip roundtrip: the
// artist typically records into headphones (fast, sealed path to the ear)
// while calibration measures the phone speaker → open air → mic loop, which
// runs a touch faster. +50 ms shifts the compensated take back in time so
// the vocal lines up with what the artist actually heard in-ear.
export const CALIBRATION_HEADPHONE_BIAS_MS = 50;
// Step size for the ±latency nudge buttons in the transport bar.
export const LATENCY_NUDGE_STEP_MS = 10;

// Punch-in crossfade (sec) — smooths the boundary between old and new takes.
export const PUNCH_FADE_SEC = 0.015;

// -------- Vocal FX (reverb + dub echo) --------------------------------------
// Wet-signal ceiling at 100% on the Reverb slider — keeps the vocal readable
// even when the artist cranks it, dubplate-style.
export const REVERB_MAX_WET = 0.55;
export const REVERB_IR_SECONDS = 1.8;
export const REVERB_IR_DECAY = 3.2;
// Classic dub echo: ~3/8-note feel at dancehall tempos, darker on each repeat.
export const DELAY_TIME_S = 0.38;
export const DELAY_FEEDBACK = 0.42;
export const DELAY_TONE_HZ = 2500;
export const DELAY_MAX_WET = 0.5;

/**
 * Generate a synthetic stereo impulse response (exponentially decaying noise)
 * for the vocal reverb ConvolverNode. Works with both live AudioContext and
 * OfflineAudioContext — no IR files to download.
 */
export function buildImpulseResponse(ctx, seconds = REVERB_IR_SECONDS, decay = REVERB_IR_DECAY) {
  const rate = ctx.sampleRate;
  const len = Math.max(1, Math.ceil(seconds * rate));
  const ir = ctx.createBuffer(2, len, rate);
  for (let c = 0; c < 2; c++) {
    const d = ir.getChannelData(c);
    for (let i = 0; i < len; i++) {
      d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
    }
  }
  return ir;
}

/**
 * Wire the vocal FX sends (reverb + dub echo) from `input` to `output`.
 * Post-fader by design: feed it the vocal GainNode so wet levels track the
 * fader/mute. Works on live and offline contexts. Returns the two wet
 * GainNodes so the live graph can adjust them as the sliders move.
 */
export function buildVocalFxChain(ctx, input, output, reverbAmt, delayAmt) {
  const convolver = ctx.createConvolver();
  convolver.buffer = buildImpulseResponse(ctx);
  const reverbWet = ctx.createGain();
  reverbWet.gain.value = reverbAmt * REVERB_MAX_WET;
  input.connect(convolver);
  convolver.connect(reverbWet);
  reverbWet.connect(output);

  const delayNode = ctx.createDelay(1.0);
  delayNode.delayTime.value = DELAY_TIME_S;
  const tone = ctx.createBiquadFilter();
  tone.type = 'lowpass';
  tone.frequency.value = DELAY_TONE_HZ;
  const feedback = ctx.createGain();
  feedback.gain.value = DELAY_FEEDBACK;
  const delayWet = ctx.createGain();
  delayWet.gain.value = delayAmt * DELAY_MAX_WET;
  input.connect(delayNode);
  delayNode.connect(tone);
  tone.connect(feedback);
  feedback.connect(delayNode);
  delayNode.connect(delayWet);
  delayWet.connect(output);

  return { reverbWet, delayWet };
}

export function detectPlatform() {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && 'ontouchend' in (typeof document !== 'undefined' ? document : {}));
  const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS/.test(ua);
  return { isIOS, isSafari };
}

export function estimateRoundtripSeconds(ctx) {
  const reported = (ctx?.outputLatency || 0) + (ctx?.baseLatency || 0);
  if (reported > 0.005) {
    // Web Audio reports OUTPUT latency only. Input (mic) latency isn't
    // exposed but is empirically the same order of magnitude, so double.
    return Math.min(reported * 2, MAX_COMPENSATION_S);
  }
  // Safari desktop + iOS Safari don't populate the latency getters — use
  // conservative empirical defaults.
  const { isIOS, isSafari } = detectPlatform();
  if (isIOS) return 0.16;      // iPhone/iPad Safari
  if (isSafari) return 0.12;   // macOS Safari
  return 0.08;                 // generic fallback (Chrome/FF usually report)
}

export function readCalibratedSeconds() {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_LATENCY_MS) : null;
    if (raw == null) return null;
    const ms = parseFloat(raw);
    if (!Number.isFinite(ms) || ms < 0 || ms > MAX_COMPENSATION_S * 1000) return null;
    return ms / 1000;
  } catch (e) { return null; }
}

export function writeCalibratedMs(ms) {
  try { localStorage.setItem(LS_LATENCY_MS, String(Math.round(ms))); } catch (e) { /* noop */ }
}

export function clearCalibratedMs() {
  try { localStorage.removeItem(LS_LATENCY_MS); } catch (e) { /* noop */ }
}

export function effectiveLatencySeconds(ctx) {
  const cal = readCalibratedSeconds();
  return cal != null ? cal : estimateRoundtripSeconds(ctx);
}

/**
 * Encode an AudioBuffer to a 320kbps MP3 blob in a dedicated Web Worker so
 * the three stems can encode in parallel without blocking the UI.
 */
export function encodeMp3(buffer, bitrate = 320) {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('../../workers/mp3Encoder.worker.js', import.meta.url));
    const left = buffer.getChannelData(0).slice();
    const right = (buffer.numberOfChannels > 1 ? buffer.getChannelData(1) : buffer.getChannelData(0)).slice();
    worker.onmessage = (e) => {
      worker.terminate();
      if (e.data.error) reject(new Error(e.data.error));
      else resolve(new Blob(e.data.parts, { type: 'audio/mpeg' }));
    };
    worker.onerror = (err) => {
      worker.terminate();
      reject(new Error(err?.message || 'MP3 encoding failed'));
    };
    worker.postMessage(
      { left, right, sampleRate: buffer.sampleRate, bitrate },
      [left.buffer, right.buffer],
    );
  });
}

/** Element-wise sum of two AudioBuffers with independent gains → new AudioBuffer.
 *  Optional `wetBuf` (reverb tail of the vocal) is summed scaled by `vocGain`
 *  so the wet level tracks the vocal fader exactly like the live graph. */
export function mixDown(ctx, instBuf, vocBuf, instGain, vocGain, samples, rate, wetBuf = null) {
  const out = ctx.createBuffer(2, samples, rate);
  for (let ch = 0; ch < 2; ch++) {
    const dst = out.getChannelData(ch);
    const a = instBuf ? instBuf.getChannelData(Math.min(ch, instBuf.numberOfChannels - 1)) : null;
    const b = vocBuf ? vocBuf.getChannelData(Math.min(ch, vocBuf.numberOfChannels - 1)) : null;
    const w = wetBuf ? wetBuf.getChannelData(Math.min(ch, wetBuf.numberOfChannels - 1)) : null;
    const aLen = a ? a.length : 0;
    const bLen = b ? b.length : 0;
    const wLen = w ? w.length : 0;
    for (let i = 0; i < samples; i++) {
      const va = i < aLen ? a[i] * instGain : 0;
      const vb = i < bLen ? b[i] * vocGain : 0;
      const vw = i < wLen ? w[i] * vocGain : 0;
      dst[i] = va + vb + vw;
    }
  }
  return out;
}

/**
 * Punch-in merge: overlays a new take onto an existing vocal clip.
 * Keeps everything from the old take, replaces only the region covered by
 * the new take, with a short crossfade at the punch boundaries to avoid clicks.
 * Returns { buffer, offset } spanning both clips.
 */
export function mergePunch(ctx, oldClip, newClip) {
  const rate = oldClip.buffer.sampleRate;
  const start = Math.min(oldClip.offset, newClip.offset);
  const end = Math.max(
    oldClip.offset + oldClip.buffer.duration,
    newClip.offset + newClip.buffer.duration,
  );
  const length = Math.ceil((end - start) * rate);
  const numCh = Math.min(2, Math.max(oldClip.buffer.numberOfChannels, newClip.buffer.numberOfChannels));
  const out = ctx.createBuffer(numCh, length, rate);

  const oldStart = Math.round((oldClip.offset - start) * rate);
  const newStart = Math.round((newClip.offset - start) * rate);
  const newLen = newClip.buffer.length;
  const fade = Math.round(PUNCH_FADE_SEC * rate);

  for (let c = 0; c < numCh; c++) {
    const dst = out.getChannelData(c);
    const oldData = oldClip.buffer.getChannelData(Math.min(c, oldClip.buffer.numberOfChannels - 1));
    const newData = newClip.buffer.getChannelData(Math.min(c, newClip.buffer.numberOfChannels - 1));
    // 1) lay down the old take in full
    dst.set(oldData, oldStart);
    // 2) overwrite the punched region with the new take,
    //    crossfading with the old audio near the boundaries (where old audio exists)
    for (let i = 0; i < newLen; i++) {
      const pos = newStart + i;
      const hasOld = pos >= oldStart && pos < oldStart + oldData.length;
      let v = newData[i];
      if (hasOld && fade > 0) {
        // fade-in at punch start, fade-out at punch end (only against existing audio)
        let t = 1;
        if (i < fade) t = i / fade;
        const tailIn = newLen - 1 - i;
        const oldExtendsPast = oldStart + oldData.length > newStart + newLen;
        if (oldExtendsPast && tailIn < fade) t = Math.min(t, tailIn / fade);
        v = v * t + oldData[pos - oldStart] * (1 - t);
      }
      dst[pos] = v;
    }
  }
  return { buffer: out, offset: start };
}

/** Copy a [fromSec, toSec) slice of an AudioBuffer into a new AudioBuffer. */
export function sliceBuffer(ctx, buffer, fromSec, toSec) {
  const rate = buffer.sampleRate;
  const start = Math.max(0, Math.floor(fromSec * rate));
  const end = Math.min(buffer.length, Math.ceil(toSec * rate));
  const out = ctx.createBuffer(buffer.numberOfChannels, Math.max(1, end - start), rate);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    out.copyToChannel(buffer.getChannelData(c).subarray(start, end), c);
  }
  return out;
}

/** Stop and forget all currently-playing BufferSources tracked in the ref. */
export function stopSources(sourcesRef) {
  sourcesRef.current.forEach((s) => { try { s.stop(); } catch (e) { /* already stopped */ } });
  sourcesRef.current = [];
}

/**
 * Schedule a clip to play against `startPos` on the timeline at ctx time `when`.
 * Handles both the case where playback starts INSIDE the clip and BEFORE it.
 * Pushes the source into `sourcesRef` so it can be stopped later.
 */
export function scheduleClip(ctx, clip, gain, startPos, when, sourcesRef) {
  if (!clip) return;
  if (startPos >= clip.offset + clip.buffer.duration) return;
  const src = ctx.createBufferSource();
  src.buffer = clip.buffer;
  src.connect(gain);
  if (startPos >= clip.offset) src.start(when, startPos - clip.offset);
  else src.start(when + (clip.offset - startPos), 0);
  sourcesRef.current.push(src);
}
